import { Select as ChakraSelect, Skeleton } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "react-query";
import { useCommand } from "../hooks/commands.hooks";
import { useConnectedUser } from "../hooks/user.hooks";
import i18n from "../i18n";
import {
	changeCommandStatus,
	updateCommand,
} from "../mutations/command.mutations";
import { CommandStatus } from "../types/command.types";
import { useSimpleToast } from "../hooks/toast.hooks";
import { getCommandMaterialContext } from "../utils/getCommandMaterialContext";
import OrderStatusBadge from "./OrderStatusBadge";

type CommandStatusBadgeReadOnlyProps = {
	id: number | string | null;
};

type CommandStatusBadgeWriteProps = CommandStatusBadgeReadOnlyProps;

type CommandStatusBadgeProps = CommandStatusBadgeReadOnlyProps & {
	editable?: boolean;
};

export const mapping = new Map<string, [string, string, string, string]>([
	[CommandStatus.DRAFT, ["#FFFFFF", "#1E293B", "#334155", "Brouillon"]],
	[
		CommandStatus.WAITING_FOR_RECEPTION,
		["#334155", "#334155", "#FFFFFF", "En attente de réception"],
	],
	[CommandStatus.RECEIVED, ["#334155", "#94A3B8", "#E2E8F0", "Réceptionnée"]],
	[
		CommandStatus.WAITING_FOR_MODELING,
		["#087443", "#AAF0C4", "#EDFCF2", "En attente de modélisation"],
	],
	[
		CommandStatus.MODELING,
		["#084C2E", "#3CCB7F", "#AAF0C4", "En modélisation"],
	],
	[
		CommandStatus.WAITING_FOR_PRINTING,
		["#052E1C", "#099250", "#3CCB7F", "Prête pour impression"],
	],
	[CommandStatus.PRINTING, ["#FFFFFF", "#095C37", "#099250", "En impression"]],
	[CommandStatus.PRINTED, ["#F6FEF9", "#052E1C", "#095C37", "Imprimé"]],
	[
		CommandStatus.WAITING_FOR_LAB,
		["#7E22CE", "#E9D5FF", "#FAF5FF", "À traiter labo"],
	],
	[
		CommandStatus.INJECTING,
		["#581C87", "#C084FC", "#E9D5FF", "En injection silicone"],
	],
	[CommandStatus.POLISHING, ["#581C87", "#9333EA", "#C084FC", "En finition"]],
	[
		CommandStatus.QUALITY_CONTROL,
		["#FAF5FF", "#6B21A8", "#9333EA", "Contrôle qualité"],
	],
	[CommandStatus.READY, ["#FCFCFD", "#581C87", "#6B21A8", "Prête"]],
	[
		CommandStatus.READY_FOR_DELIVERY,
		["#FCFCFD", "#334155", "#64748B", "Prête à expédier"],
	],
	[
		CommandStatus.PARCEL_PREPARED,
		["#FCFCFD", "#1E293B", "#475569", "Colis préparés"],
	],
	[CommandStatus.IN_DELIVERY, ["#FFFFFF", "#0C111D", "#0C111D", "Expédiée"]],
	[CommandStatus.DELIVERED, ["#FFFFFF", "#1B58F5", "#1B58F5", "Livrée"]],
	[CommandStatus.ABORTED, ["#FFFFFF", "#B91C1C", "#DC2626", "Annulée"]],
	[CommandStatus.PENDING, ["#FFFFFF", "#C2410C", "#F68511", "En pause"]],
	["HALTED", ["#FFFFFF", "#C2410C", "#F68511", "En pause"]],
]);

const CommandStatusBadgeReadOnly = ({
	id,
}: CommandStatusBadgeReadOnlyProps) => {
	const { data: command, isLoading } = useCommand(id);
	const status = command?.haltedAt ? "HALTED" : command?.status;

	if (isLoading) {
		return <Skeleton w="10rem" h="20px" />;
	}
	if (!command || status === undefined) {
		return null;
	}
	return (
		<OrderStatusBadge
			halted={command.haltedAt != null}
			status={status as CommandStatus & "HALTED"}
		></OrderStatusBadge>
	);
};

const CommandStatusBadgeWrite = ({ id }: CommandStatusBadgeWriteProps) => {
	const { data: command, isLoading } = useCommand(id);
	const status = command?.haltedAt ? "HALTED" : command?.status;
	const statusMapping = mapping.get(status ?? "");
	const user = useConnectedUser();
	const toast = useSimpleToast();
	const queryClient = useQueryClient();
	const { mutate: mutateCommandUpdate } = useMutation(updateCommand, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const { mutate: mutateCommandStatus } = useMutation(changeCommandStatus, {
		onSuccess: async () => {
			if (command?.id) {
				await queryClient.invalidateQueries();
				await queryClient.invalidateQueries(["command", command?.id]);
			}
		},
	});
	const handleHalt = (id: number) => {
		if (user?.isSuperAdmin) {
			mutateCommandStatus({
				id,
				status: "HALTED",
				reason: "",
				explanation: "",
			});
		}
	};

	if (isLoading) {
		return <Skeleton w="10rem" h="20px" />;
	}
	if (!command || !command.id) {
		return null;
	}
	return (
		<ChakraSelect
			data-no-row-link="true"
			w="fit-content"
			color={statusMapping ? statusMapping[0] : "color.700"}
			bgColor={statusMapping ? statusMapping[2] : "color.100"}
			borderColor={statusMapping ? statusMapping[1] : "color.700"}
			h="26px"
			fontSize="14px"
			borderRadius="xl"
			value={status}
			onChange={(event) => {
				if (event.target.value === "HALTED") {
					handleHalt(command.id);
				} else {
					const nextStatus = event.target.value as CommandStatus;
					if (
						nextStatus === CommandStatus.INJECTING &&
						getCommandMaterialContext(command).canInjectSilicone === false
					) {
						toast.error(
							"Command",
							"Seules les commandes avec au moins une oreille en silicone peuvent être en injection.",
						);
						return;
					}
					mutateCommandUpdate({
						id: command.id,
						status: nextStatus,
					});
				}
			}}
		>
			<option style={{ color: "black" }} value="">
				---
			</option>
			{[
				CommandStatus.DRAFT,
				CommandStatus.WAITING_FOR_RECEPTION,
				CommandStatus.RECEIVED,
				CommandStatus.WAITING_FOR_MODELING,
				CommandStatus.MODELING,
				CommandStatus.WAITING_FOR_PRINTING,
				CommandStatus.PRINTING,
				CommandStatus.PRINTED,
				CommandStatus.WAITING_FOR_LAB,
				CommandStatus.INJECTING,
				CommandStatus.POLISHING,
				CommandStatus.QUALITY_CONTROL,
				CommandStatus.READY,
				CommandStatus.READY_FOR_DELIVERY,
				CommandStatus.PARCEL_PREPARED,
				CommandStatus.IN_DELIVERY,
				CommandStatus.DELIVERED,
			].map((status) => (
				<option key={status} style={{ color: "black" }} value={status}>
					{i18n.t(`command.status.${status}`)}
				</option>
			))}
			<option style={{ color: "black" }} value="HALTED">
				En pause
			</option>
			<option style={{ color: "black" }} value={CommandStatus.ABORTED}>
				{i18n.t(`command.status.${CommandStatus.ABORTED}`)}
			</option>
		</ChakraSelect>
	);
};

const CommandStatusBadge = ({
	editable = false,
	...props
}: CommandStatusBadgeProps) => {
	if (editable) {
		return <CommandStatusBadgeWrite {...props} />;
	}
	return <CommandStatusBadgeReadOnly {...props} />;
};

export default CommandStatusBadge;
